import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Phone, Clock, Briefcase, Check, Loader2, ArrowRight, ArrowLeft, Globe, Sparkles, Plus, X } from 'lucide-react';

const INDUSTRY_TEMPLATES = {
  medspa: ['Botox & Fillers', 'Facials', 'Laser Hair Removal', 'Microneedling', 'Body Contouring', 'Consultation'],
  dental: ['Cleaning', 'Fillings', 'Crowns', 'Whitening', 'Orthodontics', 'Emergency'],
  salon: ['Haircut', 'Color', 'Highlights', 'Blowout', 'Treatment', 'Style'],
  realestate: ['Property Viewing', 'Listing Consultation', 'Buyer Consultation', 'Market Analysis'],
  fitness: ['Personal Training Session', 'Group Class', 'Consultation', 'Body Assessment'],
  legal: ['Initial Consultation', 'Document Review', 'Court Representation'],
  home_services: ['Service Quote', 'Repair Visit', 'Maintenance Check', 'Inspection'],
  other: ['Consultation', 'Service Visit'],
};

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d] = d === 'saturday' || d === 'sunday' ? { closed: true } : { open: '09:00', close: '18:00', closed: false };
  return acc;
}, {});

const TOTAL_STEPS = 4;

export default function OnboardingWizard() {
  const { api, refreshBusiness } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [provisioning, setProvisioning] = useState(false);
  const [result, setResult] = useState(null);

  const [industry, setIndustry] = useState('other');
  const [businessName, setBusinessName] = useState('');

  // Step 1
  const [services, setServices] = useState([]);
  const [customService, setCustomService] = useState('');
  // Step 2
  const [hours, setHours] = useState(DEFAULT_HOURS);
  // Step 3 (Smart customization)
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [websiteProfile, setWebsiteProfile] = useState(null);
  const [analyzeError, setAnalyzeError] = useState('');
  const [greetingStyle, setGreetingStyle] = useState('friendly');
  const [pricingPolicy, setPricingPolicy] = useState('send_quote');
  const [languages, setLanguages] = useState(['en']);
  const [leadTime, setLeadTime] = useState(2);
  const [emergencyRouting, setEmergencyRouting] = useState('take_message');
  const [mustAsk, setMustAsk] = useState([]);
  const [mustAskInput, setMustAskInput] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [faqQ, setFaqQ] = useState('');
  const [faqA, setFaqA] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const status = await api.get('/onboarding/status');
        if (status.data.completed) { navigate('/dashboard', { replace: true }); return; }
        const biz = await api.get('/business/me');
        if (biz.data?.niche) setIndustry(biz.data.niche);
        if (biz.data?.name) setBusinessName(biz.data.name);
      } catch {}
    })();
  }, [api, navigate]);

  const toggleService = (s) => setServices(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const addCustomService = () => {
    const v = customService.trim();
    if (v && !services.includes(v)) { setServices(prev => [...prev, v]); setCustomService(''); }
  };
  const updateHours = (day, field, value) => setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));

  const toggleLanguage = (code) => setLanguages(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  const addMustAsk = () => {
    const v = mustAskInput.trim();
    if (v && !mustAsk.includes(v)) { setMustAsk(prev => [...prev, v]); setMustAskInput(''); }
  };
  const addFaq = () => {
    const q = faqQ.trim(), a = faqA.trim();
    if (q && a) { setFaqs(prev => [...prev, { q, a }]); setFaqQ(''); setFaqA(''); }
  };

  const analyzeWebsite = async () => {
    if (!websiteUrl.trim()) return;
    setAnalyzing(true);
    setAnalyzeError('');
    setWebsiteProfile(null);
    try {
      const { data } = await api.post('/onboarding/analyze-website', { url: websiteUrl.trim() });
      if (data.success) {
        setWebsiteProfile(data.profile);
        // Auto-prefill: tone, services we don't have yet, languages
        if (data.profile?.tone) setGreetingStyle(data.profile.tone);
        if (Array.isArray(data.profile?.services)) {
          setServices(prev => Array.from(new Set([...prev, ...data.profile.services])));
        }
        if (Array.isArray(data.profile?.languages) && data.profile.languages.length) {
          setLanguages(prev => Array.from(new Set([...prev, ...data.profile.languages])));
        }
        if (data.profile?.pricing_signal === 'discloses') setPricingPolicy('quote_starting');
        if (data.profile?.pricing_signal === 'on_request') setPricingPolicy('send_quote');
      } else {
        setAnalyzeError("Couldn't read that site. Double-check the URL or skip this step.");
      }
    } catch (e) {
      setAnalyzeError(e.response?.data?.detail || 'Analysis failed. You can skip this step.');
    } finally {
      setAnalyzing(false);
    }
  };

  const completeOnboarding = async () => {
    setError('');
    setProvisioning(true);
    try {
      const { data } = await api.post('/onboarding/complete', {
        industry, services, hours, auto_assign_number: true,
        website_url: websiteUrl.trim() || null,
        greeting_style: greetingStyle,
        pricing_policy: pricingPolicy,
        languages,
        booking_lead_time_hours: Number(leadTime) || 0,
        emergency_routing: emergencyRouting,
        must_ask_questions: mustAsk,
        custom_faqs: faqs,
      });
      setResult(data);
      try { await refreshBusiness?.(); } catch {}
      setStep(TOTAL_STEPS + 1);
    } catch (e) {
      setError(e.response?.data?.detail || 'Setup failed. Please try again.');
    } finally {
      setProvisioning(false);
    }
  };

  const formatPhone = (n) => {
    if (!n) return '';
    const digits = n.replace(/\D/g, '');
    if (digits.length === 11) return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    return n;
  };

  const canProceed = (() => {
    if (step === 1) return services.length > 0;
    if (step === 2) return true;
    if (step === 3) return true;
    return true;
  })();

  return (
    <div className="min-h-screen bg-[#f9f8f4] dark:bg-[#0a0a0a] flex flex-col transition-colors">
      <div className="border-b border-gray-200/70 dark:border-white/5 bg-white/80 dark:bg-[#0a0a0a]/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <span className="font-heading text-lg font-semibold text-gray-900 dark:text-white">NextBell</span>
          <span className="text-xs text-gray-400 dark:text-slate-500">Step {Math.min(step, TOTAL_STEPS)} of {TOTAL_STEPS}</span>
        </div>
        <div className="h-1 bg-gray-100 dark:bg-white/[0.05]">
          <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${Math.min((step / TOTAL_STEPS) * 100, 100)}%` }} />
        </div>
      </div>

      <div className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
        {/* STEP 1: Services */}
        {step === 1 && (
          <div data-testid="onboarding-step-services">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium mb-4">
                <Briefcase className="w-3 h-3" /> Step 1 of {TOTAL_STEPS}
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white mb-3">
                What services does {businessName || 'your business'} offer?
              </h1>
              <p className="text-gray-500 dark:text-slate-400 text-sm">Ava will use these to answer questions and book appointments.</p>
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              {(INDUSTRY_TEMPLATES[industry] || INDUSTRY_TEMPLATES.other).map(s => (
                <button key={s} onClick={() => toggleService(s)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${services.includes(s) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white dark:bg-white/[0.04] text-gray-700 dark:text-slate-200 border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20'}`}>
                  {services.includes(s) && <Check className="w-3 h-3 inline mr-1" />}
                  {s}
                </button>
              ))}
            </div>
            {services.filter(s => !(INDUSTRY_TEMPLATES[industry] || []).includes(s)).map(s => (
              <span key={s} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 rounded-full text-xs mr-2 mb-2">
                {s}
                <button onClick={() => toggleService(s)} className="hover:text-purple-900 dark:hover:text-purple-200">×</button>
              </span>
            ))}
            <div className="flex gap-2 mt-4">
              <input value={customService} onChange={e => setCustomService(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCustomService()}
                placeholder="Add custom service..."
                className="flex-1 bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-lg px-4 py-2 text-sm focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500" />
              <button onClick={addCustomService} className="bg-gray-900 dark:bg-white/[0.08] dark:hover:bg-white/[0.12] text-white px-4 rounded-lg text-sm font-medium hover:bg-gray-800">Add</button>
            </div>
          </div>
        )}

        {/* STEP 2: Hours */}
        {step === 2 && (
          <div data-testid="onboarding-step-hours">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium mb-4">
                <Clock className="w-3 h-3" /> Step 2 of {TOTAL_STEPS}
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white mb-3">When are you open?</h1>
              <p className="text-gray-500 dark:text-slate-400 text-sm">Ava only books inside these hours and tells callers when you're closed.</p>
            </div>
            <div className="space-y-2 bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-3 sm:p-4">
              {DAYS.map(day => (
                <div key={day} className="flex flex-wrap items-center gap-3 py-2 border-b border-gray-100 dark:border-white/5 last:border-0">
                  <div className="w-20 sm:w-24 text-sm font-medium text-gray-700 dark:text-slate-200 capitalize">{day}</div>
                  <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-300">
                    <input type="checkbox" checked={!hours[day]?.closed} onChange={e => updateHours(day, 'closed', !e.target.checked)} />
                    Open
                  </label>
                  {!hours[day]?.closed && (
                    <>
                      <input type="time" value={hours[day]?.open || '09:00'} onChange={e => updateHours(day, 'open', e.target.value)}
                        className="bg-white dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded px-2 py-1 text-sm" />
                      <span className="text-gray-400 dark:text-slate-500 text-sm">to</span>
                      <input type="time" value={hours[day]?.close || '18:00'} onChange={e => updateHours(day, 'close', e.target.value)}
                        className="bg-white dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded px-2 py-1 text-sm" />
                    </>
                  )}
                  {hours[day]?.closed && <span className="text-sm text-gray-400 dark:text-slate-500">Closed</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP 3: Smart Customization (NEW) */}
        {step === 3 && (
          <div data-testid="onboarding-step-customize" className="space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium mb-4">
                <Sparkles className="w-3 h-3" /> Step 3 of {TOTAL_STEPS}
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white mb-3">
                Make Ava sound exactly like you
              </h1>
              <p className="text-gray-500 dark:text-slate-400 text-sm">Paste your website and we'll auto-tune Ava's voice. The rest is optional but worth 30 seconds.</p>
            </div>

            {/* Website analyzer */}
            <Card icon={Globe} title="Auto-customize from your website">
              <div className="flex flex-col sm:flex-row gap-2">
                <input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourbusiness.com"
                  className="flex-1 bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-lg px-4 py-2.5 text-sm focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500" />
                <button onClick={analyzeWebsite} disabled={analyzing || !websiteUrl.trim()} data-testid="onboarding-analyze-btn"
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg px-5 py-2.5 inline-flex items-center justify-center gap-2 whitespace-nowrap">
                  {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading...</> : <>Analyze <Sparkles className="w-4 h-4" /></>}
                </button>
              </div>
              {analyzeError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{analyzeError}</p>}
              {websiteProfile && (
                <div className="mt-3 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-xl p-3 text-xs text-gray-700 dark:text-slate-200">
                  <p className="font-medium mb-1.5 text-blue-800 dark:text-blue-200">✨ Ava learned:</p>
                  <ul className="space-y-1 list-disc list-inside text-gray-600 dark:text-slate-300">
                    {websiteProfile.tone && <li>Tone: <span className="capitalize font-medium">{websiteProfile.tone}</span></li>}
                    {websiteProfile.tagline && <li>Tagline: "{websiteProfile.tagline}"</li>}
                    {Array.isArray(websiteProfile.services) && websiteProfile.services.length > 0 && (
                      <li>{websiteProfile.services.length} services detected and added</li>
                    )}
                    {Array.isArray(websiteProfile.value_props) && websiteProfile.value_props.length > 0 && (
                      <li>{websiteProfile.value_props.length} value-prop{websiteProfile.value_props.length > 1 ? 's' : ''} captured</li>
                    )}
                    {Array.isArray(websiteProfile.faqs) && websiteProfile.faqs.length > 0 && (
                      <li>{websiteProfile.faqs.length} FAQ{websiteProfile.faqs.length > 1 ? 's' : ''} pulled</li>
                    )}
                  </ul>
                </div>
              )}
            </Card>

            {/* Greeting + Pricing */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card title="Greeting style">
                <div className="grid grid-cols-2 gap-2">
                  {[['friendly','Friendly'],['formal','Formal'],['luxury','Luxury'],['casual','Casual']].map(([v,l]) => (
                    <Pill key={v} active={greetingStyle === v} onClick={() => setGreetingStyle(v)}>{l}</Pill>
                  ))}
                </div>
              </Card>
              <Card title="When callers ask about pricing">
                <div className="space-y-2">
                  {[['quote_starting','Quote starting prices'],['send_quote','Send a custom quote'],['no_pricing','Never discuss pricing']].map(([v,l]) => (
                    <Radio key={v} active={pricingPolicy === v} onClick={() => setPricingPolicy(v)}>{l}</Radio>
                  ))}
                </div>
              </Card>
            </div>

            {/* Lead time + Emergency (Languages: English-only for now, hidden) */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card title="Min booking notice">
                <select value={leadTime} onChange={e => setLeadTime(e.target.value)}
                  className="w-full bg-white dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm">
                  <option value={0}>Same-day OK</option>
                  <option value={2}>2 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={48}>2 days</option>
                </select>
              </Card>
              <Card title="If it's an emergency">
                <select value={emergencyRouting} onChange={e => setEmergencyRouting(e.target.value)}
                  className="w-full bg-white dark:bg-white/[0.05] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm">
                  <option value="take_message">Take a detailed message</option>
                  <option value="forward_to_owner">Forward to your phone</option>
                  <option value="none">Treat like any other call</option>
                </select>
              </Card>
            </div>

            {/* Must-ask questions */}
            <Card title="Questions Ava should ALWAYS ask">
              <div className="flex flex-wrap gap-2 mb-2">
                {mustAsk.map(q => (
                  <span key={q} className="inline-flex items-center gap-1 bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-300 rounded-full px-3 py-1 text-xs">
                    {q}
                    <button onClick={() => setMustAsk(prev => prev.filter(x => x !== q))}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={mustAskInput} onChange={e => setMustAskInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addMustAsk()}
                  placeholder="e.g. allergies, insurance provider, vehicle make/model..."
                  className="flex-1 bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm focus:border-blue-500 placeholder:text-gray-400 dark:placeholder:text-slate-500" />
                <button onClick={addMustAsk} className="bg-gray-900 dark:bg-white/[0.08] text-white rounded-lg px-3 text-sm hover:bg-gray-800 dark:hover:bg-white/[0.12]"><Plus className="w-4 h-4" /></button>
              </div>
            </Card>

            {/* Custom FAQs */}
            <Card title="FAQs Ava should answer verbatim">
              {faqs.length > 0 && (
                <div className="space-y-2 mb-3">
                  {faqs.map((f, i) => (
                    <div key={i} className="bg-gray-50 dark:bg-white/[0.03] rounded-lg p-3 flex items-start gap-2">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-700 dark:text-slate-200">Q: {f.q}</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">A: {f.a}</p>
                      </div>
                      <button onClick={() => setFaqs(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
                <input value={faqQ} onChange={e => setFaqQ(e.target.value)} placeholder="Question" className="bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-400 dark:placeholder:text-slate-500" />
                <input value={faqA} onChange={e => setFaqA(e.target.value)} placeholder="Answer Ava should give" className="bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-400 dark:placeholder:text-slate-500" />
                <button onClick={addFaq} className="bg-gray-900 dark:bg-white/[0.08] text-white rounded-lg px-3 text-sm hover:bg-gray-800 dark:hover:bg-white/[0.12]"><Plus className="w-4 h-4" /></button>
              </div>
            </Card>
          </div>
        )}

        {/* STEP 4: Review & Activate */}
        {step === 4 && (
          <div data-testid="onboarding-step-review">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium mb-4">
                <Phone className="w-3 h-3" /> Final step
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white mb-3">Ready to launch Ava?</h1>
              <p className="text-gray-500 dark:text-slate-400 text-sm">When you click Activate, we'll automatically assign a dedicated business phone number and bring Ava online, usually in under 10 seconds.</p>
            </div>
            <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-5 sm:p-6 space-y-4">
              <Row label="Business" value={businessName || 'Not set'} />
              <Row label="Industry" value={industry} />
              <Row label="Services" value={services.length ? `${services.length} selected` : 'None selected'} />
              <Row label="Tone" value={greetingStyle} />
              <Row label="Languages" value="English" />
              <Row label="Pricing policy" value={pricingPolicy.replace(/_/g, ' ')} />
              <Row label="Min booking notice" value={`${leadTime} hours`} />
              <Row label="Website learned from" value={websiteProfile ? '✨ Yes' : websiteUrl ? 'Skipped' : 'No'} />
              <Row label="Phone number" value="Auto-assigned at activation" subtle />
            </div>
          </div>
        )}

        {/* STEP 5: Done */}
        {step === TOTAL_STEPS + 1 && result && (
          <div data-testid="onboarding-step-done" className="text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-green-600 dark:text-emerald-400" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white mb-3">Ava is live!</h1>
            <p className="text-gray-500 dark:text-slate-400 mb-8">Your AI receptionist is ready to take calls 24/7.</p>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-500/15 dark:to-purple-500/15 border border-blue-100 dark:border-white/10 rounded-2xl p-6 mb-6 text-left">
              <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide font-medium mb-1">Your business phone</p>
              <p className="font-heading text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-1">{formatPhone(result.phone_number)}</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">Call this number to test Ava right now</p>
            </div>

            <button onClick={() => navigate('/dashboard')}
              data-testid="onboarding-finish-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg px-8 py-3 transition-all">
              Go to Dashboard <ArrowRight className="w-4 h-4 inline ml-2" />
            </button>
          </div>
        )}

        {error && <div className="mt-4 bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>}

        {step <= TOTAL_STEPS && (
          <div className="mt-10 flex items-center justify-between gap-3">
            <button onClick={() => setStep(s => s - 1)} disabled={step === 1}
              className="flex items-center gap-1 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < TOTAL_STEPS ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canProceed}
                data-testid="onboarding-next-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg px-6 sm:px-8 py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                Continue <ArrowRight className="w-4 h-4 inline ml-2" />
              </button>
            ) : (
              <button onClick={completeOnboarding} disabled={provisioning}
                data-testid="onboarding-activate-btn"
                className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg px-6 sm:px-8 py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
                {provisioning ? <><Loader2 className="w-4 h-4 animate-spin" /> Setting up Ava...</> : <>Activate Ava <ArrowRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
        <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
      </div>
      {children}
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${active ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white dark:bg-white/[0.04] border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-200 hover:border-gray-300 dark:hover:border-white/20'}`}>
      {children}
    </button>
  );
}

function Radio({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`w-full flex items-center gap-2 text-left text-sm rounded-lg border px-3 py-2 transition-all ${active ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-500 text-blue-700 dark:text-blue-300' : 'bg-white dark:bg-white/[0.04] border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:border-gray-300 dark:hover:border-white/20'}`}>
      <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${active ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-white/20'}`}>
        {active && <span className="block w-1.5 h-1.5 bg-white rounded-full mx-auto mt-[3px]" />}
      </span>
      {children}
    </button>
  );
}

function Row({ label, value, subtle }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-white/5 last:border-0 gap-3">
      <span className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 font-medium flex-shrink-0">{label}</span>
      <span className={`text-sm capitalize text-right ${subtle ? 'text-gray-400 dark:text-slate-500' : 'text-gray-900 dark:text-white font-medium'}`}>{value}</span>
    </div>
  );
}
